import React from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';

import { IPerson } from 'components/CallScribe/Conference/usePeopleDirectory';
import usePeopleListStyles from 'components/CallScribe/Conference/peopleListStyles';
import {
  digitMatchRange,
  IMatchRange,
  nameMatchRanges,
  queryKind,
} from 'components/CallScribe/personMatch';
import { formatPhoneE164ToDisplay } from 'components/CallScribe/Recents/recentsHelpers';
import Avatar from 'components/CallScribe/ui/Avatar';

const toneFor = (seed: string): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return hash % 6;
};

export const displayPhone = (phone: string): string => formatPhoneE164ToDisplay(phone) || phone;

export const personTitle = (person: IPerson): string => person.name || displayPhone(person.phone);

export const personShortTitle = (person: IPerson): string => person.fieldName || personTitle(person);

export const personCallName = (person: IPerson): string | undefined =>
  person.fieldName || person.name || undefined;

export const personDetail = (person: IPerson): string => {
  if (person.detail && !(person.kind === 'recent' && person.name)) return person.detail;
  return person.name ? displayPhone(person.phone) : person.detail ?? '';
};

const Marked: React.FC<{ text: string; ranges: IMatchRange[]; markClass: string }> = ({
  text,
  ranges,
  markClass,
}) => {
  if (ranges.length === 0) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  let at = 0;
  ranges.forEach(r => {
    if (r.start < at) return;
    if (r.start > at) parts.push(text.slice(at, r.start));
    parts.push(<mark key={r.start} className={markClass}>{text.slice(r.start, r.end)}</mark>);
    at = r.end;
  });
  if (at < text.length) parts.push(text.slice(at));
  return <>{parts}</>;
};

const rangesFor = (text: string, isNumber: boolean, query: string): IMatchRange[] => {
  const kind = queryKind(query);
  if (kind === 'name' && !isNumber) return nameMatchRanges(text, query);
  if (kind === 'digits' && isNumber) {
    const range = digitMatchRange(text, query);
    return range ? [range] : [];
  }
  return [];
};

export interface IPersonRowProps {
  optionId: string;
  person: IPerson;
  query: string;
  active: boolean;
  selected: boolean;
  onPick: (person: IPerson) => void;
}

const PersonRow: React.FC<IPersonRowProps> = ({ optionId, person, query, active, selected, onPick }) => {
  const classes = usePeopleListStyles();
  const title = personTitle(person);
  const detail = personDetail(person);
  const titleIsNumber = !person.name;
  const detailIsNumber = Boolean(person.name) && person.kind !== 'teammate' && detail === displayPhone(person.phone);
  return (
    <div
      id={optionId}
      role="option"
      aria-selected={selected}
      aria-label={detail ? `${title}, ${detail}` : title}
      className={[classes.row, active ? classes.rowActive : '', selected ? classes.rowSelected : '']
        .filter(Boolean)
        .join(' ')}
      onMouseDown={e => { e.preventDefault(); }}
      onClick={() => { onPick(person); }}
    >
      <Avatar
        name={person.fieldName || person.name}
        tone={person.name ? toneFor(person.fieldName || person.name) : null}
        size="sm"
        icon={<HistoryRoundedIcon />}
      />
      <span className={classes.rowText}>
        <span className={classes.name}>
          <Marked text={title} ranges={rangesFor(title, titleIsNumber, query)} markClass={classes.mark} />
        </span>
        {detail ? (
          <span className={classes.detail}>
            <Marked text={detail} ranges={rangesFor(detail, detailIsNumber, query)} markClass={classes.mark} />
          </span>
        ) : null}
      </span>
      {selected ? (
        <span className={classes.tick} aria-hidden>
          <CheckRoundedIcon />
        </span>
      ) : null}
    </div>
  );
};

export default PersonRow;
